import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Link2, Loader2, Share2, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ShareableLink {
  id: string;
  label: string | null;
  expiresAt: string;
  revokedAt: string | null;
  createdAt: string;
}

interface CreateResponse {
  link: { id: string; label: string | null; expiresAt: string };
  token: string;
}

/**
 * T034a (spec 002 US3, FR-217) — Share-link button on a deployment.
 *
 * Mints + lists + revokes shareable links that bypass password / trusted-
 * IP protection. The plaintext token is shown ONCE in a copy-once modal.
 * The full URL composition (https://<deployment-host>/?of_share=<token>)
 * is done client-side because the api doesn't know which hostname to use.
 */
export function ShareLinkButton({
  deploymentId,
  deploymentUrl,
}: {
  deploymentId: string;
  /** The deployment's public URL (used to compose the shareable URL). */
  deploymentUrl: string | null | undefined;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [ttlDays, setTtlDays] = useState(7);
  const [revealed, setRevealed] = useState<{ id: string; url: string } | null>(null);

  const list = useQuery({
    enabled: open,
    queryKey: ["shareable-links", deploymentId],
    queryFn: () =>
      api
        .get<{ links: ShareableLink[] }>(`/deployments/${deploymentId}/shareable-links`)
        .then((r) => r.links),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<CreateResponse>(`/deployments/${deploymentId}/shareable-links`, {
        label: label || undefined,
        ttlDays,
      }),
    onSuccess: (r) => {
      const base = deploymentUrl ?? window.location.origin;
      const join = base.includes("?") ? "&" : "?";
      setRevealed({ id: r.link.id, url: `${base}${join}of_share=${r.token}` });
      setLabel("");
      qc.invalidateQueries({ queryKey: ["shareable-links", deploymentId] });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/deployments/${deploymentId}/shareable-links/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shareable-links", deploymentId] }),
  });

  return (
    <>
      <Button variant="default" size="sm" onClick={() => setOpen(true)}>
        <Share2 className="h-3.5 w-3.5" /> Share link
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="of-card w-full max-w-lg max-h-[80vh] overflow-auto">
            <div className="of-card-header">
              <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
                <Link2 className="h-3.5 w-3.5 text-muted-foreground" /> Shareable links
              </h3>
              <Button variant="ghost" size="sm" onClick={() => { setOpen(false); setRevealed(null); }}>
                Close
              </Button>
            </div>
            <div className="px-4 py-3 space-y-3">
              <p className="text-xs text-muted-foreground">
                Each link bypasses password + trusted-IP protection. Treat them like passwords.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-2 items-end">
                <div className="space-y-1">
                  <Label className="text-xs">Label (optional)</Label>
                  <Input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="QA / external reviewer / …"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">TTL (days)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={ttlDays}
                    onChange={(e) => setTtlDays(Math.max(1, Math.min(30, Number(e.target.value) || 7)))}
                  />
                </div>
              </div>
              {create.error instanceof ApiError && (
                <div className="text-xs text-danger">{create.error.message}</div>
              )}
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => create.mutate()}
                  disabled={create.isPending}
                >
                  {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  Create link
                </Button>
              </div>

              {revealed && <CopyableLink url={revealed.url} onClose={() => setRevealed(null)} />}

              <div className="border-t border-border-muted -mx-4 mt-2 pt-3">
                <div className="px-4 text-xs text-muted-foreground mb-2">
                  Existing links {list.data ? `(${list.data.length})` : ""}
                </div>
                {list.isLoading && (
                  <div className="px-4 text-xs text-muted-foreground">Loading…</div>
                )}
                {list.data?.length === 0 && (
                  <div className="px-4 text-xs text-muted-foreground">No links yet.</div>
                )}
                {list.data?.map((l) => (
                  <div key={l.id} className="of-row gap-3 items-start">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-mono truncate">
                        {l.label ?? "(unlabeled)"}
                      </div>
                      <div className="text-2xs text-subtle-foreground">
                        {l.revokedAt ? (
                          <span className="text-danger">Revoked {relative(l.revokedAt)}</span>
                        ) : new Date(l.expiresAt).getTime() < Date.now() ? (
                          <span className="text-muted-foreground">Expired {relative(l.expiresAt)}</span>
                        ) : (
                          <>Expires {relative(l.expiresAt)}</>
                        )}
                      </div>
                    </div>
                    {!l.revokedAt && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-danger"
                        onClick={() => revoke.mutate(l.id)}
                        disabled={revoke.isPending}
                        title="Revoke link"
                      >
                        {revoke.isPending && revoke.variables === l.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CopyableLink({ url, onClose }: { url: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="rounded-md border border-success/30 bg-success-subtle/50 p-3 space-y-2">
      <div className="text-2xs font-semibold">Share URL (shown ONCE — copy now)</div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          readOnly
          value={url}
          onClick={(e) => (e.target as HTMLInputElement).select()}
          className="flex-1 min-w-[180px] h-8 px-2 rounded-md border border-border bg-background text-xs font-mono"
        />
        <Button
          size="sm"
          variant="default"
          onClick={async () => {
            const ok = await copyText(url);
            if (ok) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }
          }}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Dismiss</Button>
      </div>
    </div>
  );
}

function relative(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const m = Math.round(abs / 60_000);
  const future = ms > 0;
  if (m < 60) return future ? `in ${m}m` : `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return future ? `in ${h}h` : `${h}h ago`;
  const d = Math.round(h / 24);
  return future ? `in ${d}d` : `${d}d ago`;
}
